/**
 * The thirty-four alert-kind display labels, the four group headers, and the
 * fixed channel column order, typed against the generated
 * `/notification-settings/get-global` response so a schema change breaks
 * this build instead of silently dropping a row.
 *
 * ISSUE-257 (the mobile screen) copies this file's labels, group headers and
 * channel order verbatim. The two must stay identical.
 *
 * `typeNotificationSettingsGetGlobalOutput200` is not re-exported from
 * `@sportsmet/notification-sdk/private`'s barrel, and the package's
 * `exports` map only exposes `./private` (no deep-path specifier), so the
 * shape is derived from the exported hook's own return type instead of an
 * import reaching past the barrel.
 */
import { type useNotificationSettingsGetGlobal } from "@sportsmet/notification-sdk/private";

type typeGetGlobalResult = ReturnType<typeof useNotificationSettingsGetGlobal>;
type typeGlobalData = NonNullable<typeGetGlobalResult["data"]>["data"];

export type typeAlertRow = NonNullable<typeGlobalData>["alerts"][number];
export type typeAlertKind = typeAlertRow["alertKind"];
export type typeAlertGroup = typeAlertRow["group"];
export type typeChannel = "email" | "inApp" | "phone";

export const ALERT_KIND_LABELS: Record<typeAlertKind, string> = {
  poolInvite: "Pool invitations",
  contestStarted: "Contest started",
  contestFinished: "Contest finished",
  seasonStarted: "Season started",
  poolSettingsChanged: "Pool settings changed",
  pickReminder: "Pick reminders",
  roundGraded: "Round results",
  eliminated: "You are eliminated",
  revived: "You are back in",
  spreadMoved: "Spread moved",
  customAwardChanged: "Custom awards",
  squaresLockUnlock: "Squares locked or unlocked",
  squaresGridPublished: "New Squares grid",
  squaresWinnerSet: "Your winning squares",
  squaresWinnerUpdated: "Winning square moved",
  squaresSquareAssigned: "A square assigned to you",
  squaresScoreCorrection: "Squares score corrections",
  stripCardLockUnlock: "Strip card locked or unlocked",
  memberWaitingApproval: "Someone is waiting for approval",
  gameStart: "Game start",
  gameEnd: "Final score",
  gameMoment: "Game updates",
  chatMessage: "New messages",
  chatReaction: "Reactions to your messages",
  messageRequest: "Message requests",
  chatClosed: "Chat closed",
  roundLocked: "Round locked",
  contestCorrected: "Result corrected",
  stripCardStripAdded: "New strip on a strip card",
  stripCardFilled: "Every strip card spot taken",
  stripCardSpotAssigned: "You got a strip card spot",
  stripCardSpotUnassigned: "You lost a strip card spot",
  stripCardWinner: "Your strip card spot won",
  stripCardGameCanceled: "Strip card game canceled",
  // ISSUE-1374.
  fanFollowRequest: "Follow requests",
  fanFollowNew: "New followers",
  fanFollowAccepted: "Follow requests approved",
};

export const ALERT_GROUP_HEADERS: Record<typeAlertGroup, string> = {
  invite: "Invites",
  pools: "Pools",
  games: "Games",
  chat: "Chat",
};

export const ALERT_GROUP_ORDER: readonly typeAlertGroup[] = [
  "invite",
  "pools",
  "games",
  "chat",
] as const;

export const CHANNEL_COLUMNS: readonly {
  channel: typeChannel;
  label: string;
}[] = [
  { channel: "email", label: "Email" },
  { channel: "inApp", label: "In-app" },
  { channel: "phone", label: "Phone" },
] as const;
